import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent, ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Adds the configured starter kit (bundle) variant to the cart and refreshes cart UI.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} upgradeButton
 * @property {HTMLElement} [upgradeButtonPrice]
 * @property {HTMLElement} [upgradeBadge]
 *
 * @extends {Component<Refs>}
 */
class StarterKitUpgradeComponent extends Component {
  requiredRefs = ['upgradeButton'];

  /** @type {boolean} */
  #pending = false;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const blockId = this.dataset.blockId;
    if (!blockId) return;

    const tinProductId = this.dataset.tinProductId;
    if (tinProductId) {
      if (event.detail.data.newProduct && event.detail.data.newProduct.id !== tinProductId) {
        return;
      }
      if (
        !event.detail.data.newProduct &&
        event.target instanceof HTMLElement &&
        event.target.dataset.productId !== tinProductId
      ) {
        return;
      }
    }

    const next = event.detail.data.html.querySelector(
      `starter-kit-upgrade-component[data-block-id="${blockId}"]`
    );

    if (!next) {
      this.hidden = true;
      return;
    }

    this.hidden = false;

    if (next instanceof HTMLElement) {
      if (next.dataset.variantId) {
        this.dataset.variantId = next.dataset.variantId;
      }
      if (next.dataset.productId) {
        this.dataset.productId = next.dataset.productId;
      }
      if (next.dataset.tinProductId) {
        this.dataset.tinProductId = next.dataset.tinProductId;
      }
    }

    morph(this, next, { childrenOnly: true });
  };

  /**
   * @param {MouseEvent} event
   */
  handleUpgrade = async (event) => {
    event.preventDefault();

    const { upgradeButton } = this.refs;
    const variantId = this.dataset.variantId;

    if (!variantId || upgradeButton.disabled || this.#pending) return;

    this.#pending = true;
    upgradeButton.disabled = true;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    for (const el of cartItemsComponents) {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    }

    const payload = {
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: sectionIds.join(','),
    };

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status) {
        this.dispatchEvent(
          new CartErrorEvent(this.id || 'starter-kit-upgrade', data.message, data.description, data.errors)
        );
        return;
      }

      const productId = this.dataset.productId || '';

      this.dispatchEvent(
        new CartAddEvent({}, String(variantId), {
          source: 'starter-kit-upgrade',
          itemCount: 1,
          productId,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.#pending = false;
      upgradeButton.disabled = false;
    }
  };
}

if (!customElements.get('starter-kit-upgrade-component')) {
  customElements.define('starter-kit-upgrade-component', StarterKitUpgradeComponent);
}
